import { ReceiptText } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { usePermissions } from '@/features/auth';
import { useMarketTimeZone } from '@/features/country-scope';
import { DataTable } from '@/shared/components/data-table/data-table';
import { DataTablePagination } from '@/shared/components/data-table/data-table-pagination';
import { DataTableViewOptions } from '@/shared/components/data-table/data-table-view-options';
import { useDataTable } from '@/shared/components/data-table/use-data-table';
import { EmptyState } from '@/shared/components/empty-state';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';

import { useFinancialTransactions } from '../api/reports.queries';
import {
  CLEAR_REPORT_FILTERS,
  hasReportFilters,
  type ReportFilterChanges,
} from '../lib/report-filters';
import type {
  FinancialTransactionParams,
  FinancialTransactionSort,
  ReportParams,
} from '../types/report.types';
import {
  TRANSACTION_HIDDEN_COLUMNS,
  TRANSACTION_SORTS,
  createTransactionColumns,
} from './financial-transactions-columns';

/** The API pages at most 100 rows. */
const MAX_PAGE_SIZE = 100;

const isSort = (value: string | null): value is FinancialTransactionSort =>
  TRANSACTION_SORTS.some((sort) => sort === value);

/**
 * The financial report's completed orders, one row each (reports.financial.read):
 * brand, denomination, payment channel, provider and the money. The Partner
 * (reports.partner.read) sees them too, without provider cost, gross profit or
 * margin, which the API leaves out, and with the partner share shown. Paged and sorted
 * on the server; its page and sort live in the URL under their own `tx*` keys so
 * they never collide with the report's. Brand, denomination, channel and provider
 * values narrow the whole report when clicked.
 */
export function FinancialTransactions({
  params,
  onFilter,
}: {
  params: ReportParams;
  onFilter: (changes: ReportFilterChanges) => void;
}) {
  const { can } = usePermissions();
  const withCost = can('reports.financial.read');
  const canRead = withCost || can('reports.partner.read');
  const canOpenOrder = can('orders.details.read');
  // All markets (no country) are reported in Palestine time.
  const zone = useMarketTimeZone(params.countryId ?? null);
  const tableState = useTableSearchParams({
    prefix: 'tx',
    defaultSort: 'date',
    defaultDirection: 'desc',
  });
  const filtered = hasReportFilters(params);

  const query: FinancialTransactionParams = {
    from: params.from,
    to: params.to,
    countryId: params.countryId,
    channel: params.channel,
    providerId: params.providerId,
    brandId: params.brandId,
    denominationId: params.denominationId,
    page: tableState.page,
    pageSize: Math.min(tableState.pageSize, MAX_PAGE_SIZE),
    sort:
      isSort(tableState.sort) && (withCost || tableState.sort !== 'grossProfit')
        ? tableState.sort
        : 'date',
    direction: tableState.direction,
  };
  const transactions = useFinancialTransactions(query, canRead);

  // TanStack needs stable columns; they change only with the permission or the zone.
  const columns = useMemo(
    () => createTransactionColumns({ onFilter, canOpenOrder, withCost, zone }),
    [onFilter, canOpenOrder, withCost, zone],
  );
  const table = useDataTable({
    data: transactions.data,
    columns,
    tableState,
    getRowId: (row) => String(row.orderId),
    // The Partner's money is the discount, channel fee and its share: none is hidden.
    initialColumnVisibility: withCost ? TRANSACTION_HIDDEN_COLUMNS : undefined,
  });

  if (!canRead) return null;

  return (
    <section aria-labelledby="financial-transactions-title" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 id="financial-transactions-title" className="text-sm font-semibold">
            Transactions
          </h3>
          <p className="text-muted-foreground text-xs">
            Each completed order: what was bought, how it was paid and which provider fulfilled it.
            Click a brand, denomination, channel or provider to filter the report.
          </p>
        </div>
        <span data-print="hide">
          <DataTableViewOptions table={table} />
        </span>
      </div>
      <DataTable
        table={table}
        isLoading={transactions.isLoading}
        isFetching={transactions.isFetching}
        error={transactions.error}
        onRetry={() => void transactions.refetch()}
        emptyState={
          filtered ? (
            <EmptyState
              icon={ReceiptText}
              title="No transactions match these filters"
              description="No completed order in the period has this channel, provider, brand or denomination."
              action={
                <Button variant="outline" size="sm" onClick={() => onFilter(CLEAR_REPORT_FILTERS)}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={ReceiptText}
              title="No completed orders in this period"
              description="Try a longer period, another country, or all your countries."
            />
          )
        }
      />
      <div data-print="hide">
        <DataTablePagination table={table} />
      </div>
    </section>
  );
}
